import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Key,
  Loader2,
  CheckCircle,
  XCircle,
  RefreshCw,
  Save,
  Plus,
  Trash2,
  BookOpen,
  Sparkles,
  Brain,
} from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@easyflow/ui-frontend/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api';
import { getErrorMessage } from '../../lib/utils';


interface ClaudeConfig {
  id: string;
  tenant_id: string;
  api_key_hint: string;
  is_active: boolean;
  connection_valid: boolean;
  last_tested_at: string | null;
  api_version: string;
}

interface ClaudeSkill {
  id: string;
  tenant_id: string;
  anthropic_skill_id: string;
  display_title: string;
  description: string | null;
  source: string;
  latest_version: string | null;
  created_at: string;
  updated_at: string;
}

export default function ClaudeAIConfigTab() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState<ClaudeConfig | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; error?: string; models?: string[] } | null>(null);

  const [apiKey, setApiKey] = useState('');
  const [isActive, setIsActive] = useState(false);
  const initialLoadDone = useRef(false);
  const [savingToggle, setSavingToggle] = useState(false);

  const [skills, setSkills] = useState<ClaudeSkill[]>([]);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [showCreateSkill, setShowCreateSkill] = useState(false);
  const [newSkillTitle, setNewSkillTitle] = useState('');
  const [newSkillDescription, setNewSkillDescription] = useState('');
  const [newSkillContent, setNewSkillContent] = useState('');
  const [creatingSkill, setCreatingSkill] = useState(false);
  const [deletingSkillId, setDeletingSkillId] = useState<string | null>(null);
  const [selectedSkill, setSelectedSkill] = useState<ClaudeSkill | null>(null);
  const [skillDetailLoading, setSkillDetailLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const configRes = await apiClient.get('/ai-tools/claude-config');
      const configData = (configRes?.data ?? configRes) as ClaudeConfig | null;
      setConfig(configData);
      if (configData) {
        setIsActive(configData.is_active);
        if (configData.last_tested_at && !testResult) {
          setTestResult({
            success: configData.connection_valid,
            models: configData.connection_valid ? ['Connessione verificata'] : undefined,
            error: configData.connection_valid ? undefined : 'Ultimo test fallito',
          });
        }
      }
      initialLoadDone.current = true;
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [testResult, toast]);

  const fetchSkills = async () => {
    setSkillsLoading(true);
    try {
      const res = await apiClient.get('/ai-tools/claude-config/skills');
      const skillsData = res?.data || res;
      setSkills(Array.isArray(skillsData) ? skillsData : []);
    } catch (error) {
      console.warn('Skills fetch failed:', getErrorMessage(error));
      setSkills([]);
    } finally {
      setSkillsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchSkills();
  }, [fetchData]);

  const handleToggleActive = useCallback(async (checked: boolean) => {
    setIsActive(checked);
    if (initialLoadDone.current && config) {
      setSavingToggle(true);
      try {
        await apiClient.put('/ai-tools/claude-config', { is_active: checked });
      } catch (error) {
        toast({ title: 'Errore salvataggio', description: getErrorMessage(error), variant: 'destructive' });
        await fetchData();
      } finally {
        setSavingToggle(false);
      }
    }
  }, [config, toast, fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: { is_active: boolean; api_key?: string } = {
        is_active: isActive,
      };
      if (apiKey) {
        payload.api_key = apiKey;
      }
      await apiClient.put('/ai-tools/claude-config', payload);
      toast({ title: 'Salvato', description: 'Configurazione Claude AI aggiornata' });
      setApiKey('');
      await fetchData();
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      if (!apiKey && !config?.api_key_hint) {
        toast({ title: 'Errore', description: 'Inserisci una API Key per testare la connessione', variant: 'destructive' });
        setTesting(false);
        return;
      }
      const res = await apiClient.post('/ai-tools/claude-config/test', {
        api_key: apiKey || undefined,
      });
      const raw = res?.data || res;
      const result = ((raw as { data?: unknown })?.data || raw) as { success: boolean; models?: string[]; error?: string };
      setTestResult(result);
      if (result.success) {
        toast({ title: 'Connessione OK', description: `${result.models?.length || 0} modelli disponibili` });
        await fetchData();
      } else {
        toast({ title: 'Connessione fallita', description: result.error, variant: 'destructive' });
        await fetchData();
      }
    } catch (error) {
      setTestResult({ success: false, error: getErrorMessage(error) });
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setTesting(false);
    }
  };

  const handleCreateSkill = async () => {
    if (!newSkillTitle.trim() || !newSkillContent.trim()) {
      toast({ title: 'Errore', description: 'Titolo e contenuto sono obbligatori', variant: 'destructive' });
      return;
    }
    setCreatingSkill(true);
    try {
      await apiClient.post('/ai-tools/claude-config/skills', {
        display_title: newSkillTitle,
        description: newSkillDescription,
        content: newSkillContent,
      });
      toast({ title: 'Skill creata', description: `"${newSkillTitle}" creata su Anthropic` });
      setNewSkillTitle('');
      setNewSkillDescription('');
      setNewSkillContent('');
      setShowCreateSkill(false);
      await fetchSkills();
    } catch (error) {
      toast({ title: 'Errore creazione skill', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setCreatingSkill(false);
    }
  };

  const handleViewSkillDetail = async (skill: ClaudeSkill) => {
    setSelectedSkill(skill);
    setSkillDetailLoading(true);
    try {
      const res = await apiClient.get(`/ai-tools/claude-config/skills/${skill.id}`);
      const detailData = (res?.data ?? res) as ClaudeSkill | null;
      if (detailData) {
        setSelectedSkill(detailData);
      }
    } catch (error) {
      console.warn('Skill detail fetch failed:', getErrorMessage(error));
    } finally {
      setSkillDetailLoading(false);
    }
  };

  const handleDeleteSkill = async (skill: ClaudeSkill) => {
    setDeletingSkillId(skill.id);
    try {
      await apiClient.delete(`/ai-tools/claude-config/skills/${skill.id}`);
      toast({ title: 'Skill eliminata', description: `"${skill.display_title}" rimossa` });
      await fetchSkills();
    } catch (error) {
      toast({ title: 'Errore eliminazione', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setDeletingSkillId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Brain className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Claude AI (Anthropic)</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Provider AI Anthropic — il modello si sceglie nella configurazione agente</p>
            </div>
            {skills.length > 0 && (
              <Badge className="bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border border-purple-200 dark:border-purple-800 text-xs">
                {skills.length} skill{skills.length !== 1 ? 's' : ''} attive
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Label htmlFor="claude-active" className="text-sm text-gray-700 dark:text-gray-300">Attivo</Label>
            <Switch
              id="claude-active"
              checked={isActive}
              onCheckedChange={handleToggleActive}
              disabled={savingToggle}
            />
            {isActive ? (
              <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Attivo</Badge>
            ) : (
              <Badge className="bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">Disattivato</Badge>
            )}
          </div>
        </div>

        <div className="mb-4">
          <Label className="text-gray-700 dark:text-gray-300 text-sm">API Key</Label>
          <div className="relative mt-1">
            <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={config?.api_key_hint || 'sk-ant-api03-...'}
              className="pl-10 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
            />
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={handleTest}
            disabled={testing || (!apiKey && !config?.api_key_hint)}
            variant="outline"
            className="border-purple-300 text-purple-700 hover:bg-purple-50 dark:border-purple-600 dark:text-purple-400 dark:hover:bg-purple-900/20"
          >
            {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Test Connessione
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Salva Configurazione
          </Button>
        </div>

        <div className={`mt-4 p-3 rounded-lg border ${config?.connection_valid ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' : config?.last_tested_at ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800' : 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700'}`}>
          <div className="flex items-center gap-2">
            {config?.connection_valid ? (
              <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
            ) : config?.last_tested_at ? (
              <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
            ) : (
              <XCircle className="h-4 w-4 text-gray-400" />
            )}
            <span className={`text-sm font-medium ${config?.connection_valid ? 'text-green-700 dark:text-green-400' : config?.last_tested_at ? 'text-red-700 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
              Stato: {config?.connection_valid ? 'Connesso' : config?.last_tested_at ? 'Non connesso' : 'Mai testato'}
            </span>
            {config?.last_tested_at && (
              <span className="text-xs text-gray-400 ml-auto">
                Ultimo test: {new Date(config.last_tested_at).toLocaleString('it-IT')}
              </span>
            )}
          </div>
        </div>

        {testResult && (
          <div className={`mt-3 p-3 rounded-lg border ${testResult.success ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'}`}>
            <div className="flex items-center gap-2">
              {testResult.success ? (
                <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
              ) : (
                <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
              )}
              <span className={`text-sm font-medium ${testResult.success ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                {testResult.success ? 'Connessione riuscita' : 'Connessione fallita'}
              </span>
            </div>
            {testResult.success && testResult.models && testResult.models.length > 0 && testResult.models[0] !== 'Connessione verificata' && (
              <div className="mt-2 space-y-1">
                <p className="text-xs font-medium text-green-700 dark:text-green-400">
                  {testResult.models.length} modelli disponibili:
                </p>
                <div className="flex flex-wrap gap-1">
                  {testResult.models.slice(0, 8).map((model, i) => (
                    <Badge key={i} className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800 text-[10px]">
                      {model}
                    </Badge>
                  ))}
                  {testResult.models.length > 8 && (
                    <Badge className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-[10px]">
                      +{testResult.models.length - 8} altri
                    </Badge>
                  )}
                </div>
              </div>
            )}
            {!testResult.success && testResult.error && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">{testResult.error}</p>
            )}
          </div>
        )}
      </div>

      <div className="bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800 rounded-lg p-3 text-xs text-purple-800 dark:text-purple-300">
        <strong>Funzionalità avanzate per agente:</strong> Tool Search, Memory Tool, Programmatic Tool Calling, Token-Efficient Tools, Fine-grained Tool Streaming e disabilitazione del parallel tool use si configurano dentro il modale di ogni singolo agente (Chatbot / Personal Assistant) quando il provider è Claude.
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <BookOpen className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Skills Manager</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Gestisci le Skills Anthropic (Beta API). Crea, visualizza e elimina skills.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={fetchSkills}
              variant="outline"
              size="sm"
              disabled={skillsLoading}
              className="border-purple-300 text-purple-700 dark:border-purple-600 dark:text-purple-400"
            >
              {skillsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
            <Button
              onClick={() => setShowCreateSkill(true)}
              size="sm"
              disabled={!isActive || !config?.api_key_hint}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Plus className="h-4 w-4 mr-1" />
              Nuova Skill
            </Button>
          </div>
        </div>

        {!isActive || !config?.api_key_hint ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Brain className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Configura e attiva Claude per gestire le Skills</p>
          </div>
        ) : skillsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
          </div>
        ) : skills.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Sparkles className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Nessuna skill trovata. Crea la prima!</p>
          </div>
        ) : (
          <div className="space-y-2">
            {skills.map((skill) => (
              <div key={skill.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" onClick={() => handleViewSkillDetail(skill)}>
                <div className="flex items-center gap-3">
                  <BookOpen className="h-4 w-4 text-purple-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{skill.display_title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        ID: {skill.anthropic_skill_id.slice(0, 16)}...
                      </span>
                      <Badge variant="outline" className="text-xs px-1.5 py-0">
                        {skill.source}
                      </Badge>
                      {skill.description && (
                        <span className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-[200px]">
                          {skill.description}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <Button
                  onClick={(e) => { e.stopPropagation(); handleDeleteSkill(skill); }}
                  variant="ghost"
                  size="sm"
                  disabled={deletingSkillId === skill.id}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  {deletingSkillId === skill.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!selectedSkill} onOpenChange={(open) => { if (!open) setSelectedSkill(null); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-purple-500" />
              Dettaglio Skill
            </DialogTitle>
          </DialogHeader>
          {skillDetailLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
            </div>
          ) : selectedSkill ? (
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs text-gray-500 dark:text-gray-400">Titolo</Label>
                <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{selectedSkill.display_title}</p>
              </div>
              {selectedSkill.description && (
                <div>
                  <Label className="text-xs text-gray-500 dark:text-gray-400">Descrizione</Label>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">{selectedSkill.description}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500 dark:text-gray-400">Anthropic Skill ID</Label>
                  <p className="text-xs font-mono text-gray-600 dark:text-gray-400 mt-1 break-all">{selectedSkill.anthropic_skill_id}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500 dark:text-gray-400">Sorgente</Label>
                  <Badge variant="outline" className="mt-1">{selectedSkill.source}</Badge>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500 dark:text-gray-400">Creata il</Label>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{new Date(selectedSkill.created_at).toLocaleString('it-IT')}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500 dark:text-gray-400">Ultimo aggiornamento</Label>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{new Date(selectedSkill.updated_at).toLocaleString('it-IT')}</p>
                </div>
              </div>
              {selectedSkill.latest_version && (
                <div>
                  <Label className="text-xs text-gray-500 dark:text-gray-400">Versione</Label>
                  <p className="text-xs font-mono text-gray-600 dark:text-gray-400 mt-1">{selectedSkill.latest_version}</p>
                </div>
              )}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedSkill(null)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCreateSkill} onOpenChange={setShowCreateSkill}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-500" />
              Crea Nuova Skill Anthropic
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm">Titolo Skill</Label>
              <Input
                value={newSkillTitle}
                onChange={(e) => setNewSkillTitle(e.target.value)}
                placeholder="es. Customer Support Expert"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm">Descrizione (opzionale)</Label>
              <Input
                value={newSkillDescription}
                onChange={(e) => setNewSkillDescription(e.target.value)}
                placeholder="Breve descrizione della skill"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm">Contenuto SKILL.md</Label>
              <Textarea
                value={newSkillContent}
                onChange={(e) => setNewSkillContent(e.target.value)}
                placeholder="# Skill Name&#10;&#10;Istruzioni per Claude..."
                rows={10}
                className="mt-1 font-mono text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateSkill(false)}>Annulla</Button>
            <Button
              onClick={handleCreateSkill}
              disabled={creatingSkill || !newSkillTitle.trim() || !newSkillContent.trim()}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {creatingSkill ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Crea Skill
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
